import { useCallback, useEffect, useState } from 'react';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import { VehicleService } from '../../services/vehicles/vehicleService';

/*----------------------------------------------------------------------------------------
* EN: Hook to manage vehicle fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de vehículos.
----------------------------------------------------------------------------------------*/

const useVehicles = (initialFilters = {}) => {
    // STATES
    const { userCan } = usePrivilege();
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    let filters = {
        limit: 999999,
        filter_filters: { ...initialFilters }
    };

    const fetchVehicles = useCallback(async (f?: any) => {
        if (!userCan('list', 'vehicle')) {
            setError('No tienes permisos para listar vehículos');
            return;
        }

        const newFilters = { ...filters, filter_filters: { ...filters.filter_filters, ...f } };
        setLoading(true);
        setError(null);

        try {
            const service = new VehicleService();
            const response = await service.listSelectVehicles(newFilters);
            const fetchedData = response.getResponseData();

            if (fetchedData && fetchedData?.data) {
                const mappedData = fetchedData.data?.map((vehicle: {
                    id: string;
                    plateNumber: string;
                    brand?: string;
                    model?: string;
                    companyId?: string | null;
                    companyName?: string | null;
                }) => ({
                    value: vehicle.id,
                    label: vehicle.plateNumber + (vehicle.brand && vehicle.model ? ` (${vehicle.brand} ${vehicle.model})` : ''),
                    companyId: vehicle.companyId ?? null,
                    companyName: vehicle.companyName ?? null,
                }));
                setData(mappedData || []);
                return mappedData;
            } else {
                setData([]);
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Error al obtener los vehículos';
            setError(errorMessage);
            console.error('Error fetching vehicles:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [userCan]);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    const getVehiclesList = (statusFilter?: string | null, notShowIds?: Array<string> | null) => {
        let dataCopy = [...data];

        // Filtrar por estado si se proporciona
        if (statusFilter) {
            dataCopy = dataCopy.filter((vehicle: any) => vehicle.status === statusFilter);
        }

        // Excluir IDs específicos si se proporcionan
        if (notShowIds && Array.isArray(notShowIds)) {
            dataCopy = dataCopy.filter((vehicle: any) => !notShowIds.includes(vehicle.value));
        }

        return dataCopy;
    };

    const refreshVehicles = useCallback(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    return {
        data,
        vehiclesLoading: loading,
        error,
        fetchVehicles,
        getVehiclesList,
        refreshVehicles
    };
};

export default useVehicles;